// Implements: F-CNV-003, F-UIX-008, F-UIX-009, F-UIX-007, F-UIX-017
/**
 * The field that starts a turn. Pure presentation.
 *
 * The typed text is a prop: it must survive a refused turn (F-UIX-008) and only the
 * container knows the turn was refused. The refusal is the server's own sentence for
 * a busy mockup (`CONFLICT`) or a process at capacity (`UNAVAILABLE`), which are
 * written for the person; any other failure reads the generic sentence.
 *
 * Ctrl+Enter (Cmd+Enter on a Mac) sends from the field, because Enter alone writes a
 * new line (F-UIX-017, the sending part). While a turn is open nothing can be sent.
 *
 * The redesign's composer (UX-05, the mockup's `.comp-box`): the field without a frame of its own
 * inside a box that shows the focus, Modificar / Regenerar desde cero as a segmented control, the
 * reference image behind the clip, an icon send button, and under the box the brief review as a
 * switch next to the hint. Refusals stay under the box, read out as alerts.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Switch } from "../../../shared/ui/atoms/Switch";
import { describedBy, errorId, hintId } from "../../../shared/ui/molecules/Field";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { START_TURN_REFUSAL_CODES, userMessage } from "../../../shared/ui/molecules/userMessage";
import type { TurnMode } from "../../../types/api";
import type { ApiFailure } from "../../../types/errors";
import styles from "./TurnComposer.module.css";

export const COMPOSER_FIELD_ID = "conversation-message";
export const IMAGE_FIELD_ID = "conversation-reference-image";

/** Why a chosen file was not attached: only the quick pre-checks; the server judges again. */
export type ImageRefusal = "notSupported" | "tooBig" | "unreadable";

const IMAGE_REFUSALS = {
  notSupported: "conversation.composer.image.notSupported",
  tooBig: "conversation.composer.image.tooBig",
  unreadable: "conversation.composer.image.unreadable",
} as const;

export interface TurnComposerProps {
  readonly text: string;
  readonly onTextChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** A turn is open: nothing can be sent until it ends. */
  readonly disabled: boolean;
  /** Disabled by a rate-limit wait (shown by the container), not by an open turn. */
  readonly waiting?: boolean | undefined;
  /** The refusal of the last attempt, if any. */
  readonly error: ApiFailure | null;
  /**
   * What the next message does over the version there is (F-CNV-010). Absent when there is
   * no version yet: then nothing can be chosen, and nothing is sent.
   */
  readonly mode?: { readonly value: TurnMode; readonly onChange: (mode: TurnMode) => void } | undefined;
  /**
   * The opt-in to review the brief before anything is drawn (F-CNV-016, OD-8). Absent when the
   * turn will not draw from scratch (a modification has no brief): then it is not offered.
   */
  readonly reviewBrief?:
    | { readonly checked: boolean; readonly onChange: (checked: boolean) => void }
    | undefined;
  /**
   * The optional reference image (F-CNV-012, OD-11): at most one. `name` is the attached file's
   * name (`null` when none); `refusal` is why the last chosen file was not attached.
   */
  readonly image?:
    | {
        readonly name: string | null;
        readonly refusal: ImageRefusal | null;
        readonly onSelect: (file: File) => void;
        readonly onRemove: () => void;
      }
    | undefined;
}

const MODES: readonly { readonly value: TurnMode; readonly label: "conversation.composer.mode.modify" | "conversation.composer.mode.regenerate" }[] = [
  { value: "modify", label: "conversation.composer.mode.modify" },
  { value: "regenerate", label: "conversation.composer.mode.regenerate" },
];

export function TurnComposer({
  text,
  onTextChange,
  onSubmit,
  disabled,
  waiting = false,
  error,
  mode,
  reviewBrief,
  image,
}: TurnComposerProps): ReactElement {
  const { t } = useTranslation();
  const empty = text.trim() === "";
  const send = (): void => {
    if (!disabled && !empty) onSubmit();
  };
  const hint = disabled && !waiting ? t("conversation.composer.busy") : t("conversation.composer.hint");
  const refusal =
    error === null ? undefined : userMessage(error, t("conversation.composer.failed"), START_TURN_REFUSAL_CODES);
  const imageError = image?.refusal == null ? undefined : t(IMAGE_REFUSALS[image.refusal]);

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        // Inside the application, never a native navigation (DD22).
        event.preventDefault();
        send();
      }}
    >
      <div className={styles.box}>
        <label className={styles.srOnly} htmlFor={COMPOSER_FIELD_ID}>
          {t("conversation.composer.label")}
        </label>
        <Textarea
          id={COMPOSER_FIELD_ID}
          variant="bare"
          autoGrow
          value={text}
          onChange={onTextChange}
          onSubmitShortcut={send}
          disabled={disabled}
          invalid={refusal !== undefined}
          placeholder={t("conversation.composer.placeholder")}
          describedBy={describedBy(COMPOSER_FIELD_ID, { hint, error: refusal })}
        />
        {image?.name == null ? null : (
          <p className={styles.attached}>
            <Icon name="clip" size="sm" />
            <span>{image.name}</span>
            <Button variant="ghost" size="sm" disabled={disabled} onClick={image.onRemove}>
              {t("conversation.composer.image.remove")}
            </Button>
          </p>
        )}
        <div className={styles.bar}>
          {mode === undefined ? null : (
            <SegmentedControl<TurnMode>
              label={t("conversation.composer.mode.label")}
              options={MODES.map((option) => ({ value: option.value, label: t(option.label) }))}
              value={mode.value}
              onChange={mode.onChange}
              disabled={disabled}
            />
          )}
          <span className={styles.spacer} />
          {image === undefined ? null : (
            <label className={styles.attach} title={t("conversation.composer.image.hint")}>
              <Icon name="clip" />
              <input
                id={IMAGE_FIELD_ID}
                className={styles.srOnly}
                type="file"
                accept="image/png,image/jpeg"
                aria-label={t("conversation.composer.image.label")}
                aria-describedby={describedBy(IMAGE_FIELD_ID, {
                  hint: t("conversation.composer.image.hint"),
                  error: imageError,
                })}
                disabled={disabled}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared so choosing the same file again (after removing it) fires a change.
                  const input = event.target;
                  if (file !== undefined) image.onSelect(file);
                  input.value = "";
                }}
              />
            </label>
          )}
          <Button
            variant="primary"
            iconOnly
            type="submit"
            label={t("conversation.composer.submit")}
            keyShortcuts="Control+Enter Meta+Enter"
            disabled={disabled || empty}
          >
            <Icon name="send" />
          </Button>
        </div>
      </div>
      {refusal === undefined ? null : (
        <p id={errorId(COMPOSER_FIELD_ID)} className={styles.error} role="alert">
          {refusal}
        </p>
      )}
      {imageError === undefined ? null : (
        <p id={errorId(IMAGE_FIELD_ID)} className={styles.error} role="alert">
          {imageError}
        </p>
      )}
      <span id={hintId(IMAGE_FIELD_ID)} className={styles.srOnly}>
        {t("conversation.composer.image.hint")}
      </span>
      <div className={styles.foot}>
        {reviewBrief === undefined ? null : (
          <Switch
            label={t("conversation.composer.reviewBrief.label")}
            checked={reviewBrief.checked}
            onChange={reviewBrief.onChange}
            disabled={disabled}
          />
        )}
        <span id={hintId(COMPOSER_FIELD_ID)} className={styles.hint}>
          {hint}
        </span>
      </div>
    </form>
  );
}
